import axios from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext"

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000"

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("accessToken")
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...getExhibitionHeader(),
  }
}

export interface CreatePrintJobResponse {
  success: boolean
  print_job_id: number
  print_token: string
  expires_at: string
  download_url: string
  message?: string
}

export interface PrintJobStatusResponse {
  success: boolean
  print_job_id: number
  status: "queued" | "uploading" | "ready" | "printing" | "completed" | "failed"
  printer: string
  workstation: string
  created_at: string
  expires_at: string
  completed_at: string | null
  failure_reason: string
}

export const securePrintService = {
  /**
   * Upload the in-memory generated badge file and open a Print Job.
   * `file` should be a Blob/File built from the canvas dataURL — never
   * written to disk and never exposed to the user.
   */
  async createPrintJob(params: {
    badgeTrackingId: number | string
    file: Blob
    filename: string
    printer: string
    workstation: string
  }): Promise<CreatePrintJobResponse> {
    const formData = new FormData()
    formData.append("badge_tracking_id", String(params.badgeTrackingId))
    formData.append("printer", params.printer)
    formData.append("workstation", params.workstation)
    formData.append("file", params.file, params.filename)

    const response = await axios.post(`${API_BASE_URL}/api/badges/print-jobs/`, formData, {
      headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
    })
    return response.data
  },

  /**
   * Retrieves the temporary printable file exactly once, using the secure
   * single-use token. The result is handed directly to QZ Tray for printing
   * and never rendered, downloaded, or shown to the operator.
   */
  async fetchPrintableFile(printJobId: number, token: string): Promise<Blob> {
    const response = await axios.get(
      `${API_BASE_URL}/api/badges/print-jobs/${printJobId}/download/${token}/`,
      { headers: authHeaders(), responseType: "blob" }
    )
    return response.data
  },

  async reportCompleted(printJobId: number, printer: string): Promise<void> {
    await axios.post(
      `${API_BASE_URL}/api/badges/print-jobs/${printJobId}/completed/`,
      { printer },
      { headers: authHeaders() }
    )
  },

  async reportFailed(printJobId: number, reason: string): Promise<void> {
    await axios.post(
      `${API_BASE_URL}/api/badges/print-jobs/${printJobId}/failed/`,
      { reason },
      { headers: authHeaders() }
    )
  },

  async getStatus(printJobId: number): Promise<PrintJobStatusResponse> {
    const response = await axios.get(
      `${API_BASE_URL}/api/badges/print-jobs/${printJobId}/status/`,
      { headers: authHeaders() }
    )
    return response.data
  },
}
